import { useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Trash2, Plus } from "lucide-react";
import { useParticipantOptions } from "@/hooks/useParticipantOptions";

export type LocalParticipantOption = {
  label: string;
  quantity: number;
};

type ParticipantOptionsEditorProps =
  | {
      leadId: string;
      localOptions?: never;
      setLocalOptions?: never;
      isSnapStack?: boolean;
    }
  | {
      leadId: null;
      localOptions: LocalParticipantOption[];
      setLocalOptions: React.Dispatch<React.SetStateAction<LocalParticipantOption[]>>;
      isSnapStack?: boolean;
    };

export function ParticipantOptionsEditor(props: ParticipantOptionsEditorProps) {
  const { leadId, isSnapStack } = props;

  if (leadId) {
    return <ConnectedEditor leadId={leadId} isSnapStack={isSnapStack} />;
  }

  return (
    <LocalEditor
      localOptions={props.localOptions!}
      setLocalOptions={props.setLocalOptions!}
      isSnapStack={isSnapStack}
    />
  );
}

function ConnectedEditor({ leadId, isSnapStack }: { leadId: string; isSnapStack?: boolean }) {
  const { options, isLoading, addOption, updateOption, deleteOption } = useParticipantOptions(leadId);
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const handleUpdate = useCallback(
    (id: string, updates: { label?: string; quantity?: number }) => {
      if (debounceTimers.current[id]) clearTimeout(debounceTimers.current[id]);
      debounceTimers.current[id] = setTimeout(() => {
        updateOption.mutate({ id, updates });
      }, 500);
    },
    [updateOption]
  );

  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, []);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading options…</p>;

  return (
    <div className="space-y-2">
      <Label>{isSnapStack ? "Unit Options" : "Participant Options"}</Label>
      <div className="border rounded-md divide-y">
        {options.map((opt) => (
          <div key={opt.id} className="flex items-center gap-2 p-2">
            <Input
              defaultValue={opt.label}
              className="flex-1"
              onChange={(e) => handleUpdate(opt.id, { label: e.target.value })}
            />
            <Input
              type="number"
              min={1}
              max={1000}
              defaultValue={opt.quantity}
              className="w-24"
              onChange={(e) =>
                handleUpdate(opt.id, { quantity: parseInt(e.target.value) || 1 })
              }
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={options.length <= 1 || deleteOption.isPending}
              onClick={() => deleteOption.mutate(opt.id)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1"
        onClick={() => addOption.mutate()}
        disabled={addOption.isPending}
      >
        <Plus className="h-3.5 w-3.5" /> Add Option
      </Button>
    </div>
  );
}

function LocalEditor({
  localOptions,
  setLocalOptions,
  isSnapStack,
}: {
  localOptions: LocalParticipantOption[];
  setLocalOptions: React.Dispatch<React.SetStateAction<LocalParticipantOption[]>>;
  isSnapStack?: boolean;
}) {
  const updateLocal = (index: number, updates: Partial<LocalParticipantOption>) => {
    setLocalOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, ...updates } : opt))
    );
  };

  const addLocal = () => {
    setLocalOptions((prev) => [
      ...prev,
      { label: `Option ${prev.length + 1}`, quantity: 1 },
    ]);
  };

  const removeLocal = (index: number) => {
    if (localOptions.length <= 1) return;
    setLocalOptions((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <Label>{isSnapStack ? "Unit Options" : "Participant Options"}</Label>
      <div className="border rounded-md divide-y">
        {localOptions.map((opt, i) => (
          <div key={i} className="flex items-center gap-2 p-2">
            <Input
              value={opt.label}
              className="flex-1"
              onChange={(e) => updateLocal(i, { label: e.target.value })}
            />
            <Input
              type="number"
              min={1}
              max={1000}
              value={opt.quantity}
              className="w-24"
              onChange={(e) =>
                updateLocal(i, { quantity: parseInt(e.target.value) || 1 })
              }
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={localOptions.length <= 1}
              onClick={() => removeLocal(i)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1"
        onClick={addLocal}
      >
        <Plus className="h-3.5 w-3.5" /> Add Option
      </Button>
    </div>
  );
}
